import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('lumero_token'));
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setForcePasswordChange(response.data.force_password_change || false);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, force_password_change } = response.data;
    
    localStorage.setItem('lumero_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setForcePasswordChange(force_password_change || false);
    
    return { userData, force_password_change };
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    setForcePasswordChange(false);
    // Update user state
    await fetchUser();
  };

  const logout = () => {
    localStorage.removeItem('lumero_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setForcePasswordChange(false);
  };

  // Role helpers
  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'owner'; // Alias for owner
  const isSetter = user?.role === 'setter';

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout,
      changePassword,
      forcePasswordChange,
      isOwner, 
      isAdmin,
      isSetter 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
